import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "../../Assets/svgs/AddIcon";
import CollapseIcon from "../../Assets/svgs/CollapseIcon";
import DeleteIcon from "../../Assets/svgs/DeleteIcon";
import FunctionIcon from "../../Assets/svgs/FunctionIcon";
import SolidTrashIcon from "../../Assets/svgs/SolidTrashIcon";
import UpArrowIcon from "../../Assets/svgs/UpArrowIcon";
import { Loader } from "../../Common/Loader";
import { uuidGenerator } from "../../Common/utils";
import { validURL } from "../../Common/Validations";
import { ConfirmModel } from "../../Model/ConfirmModel";
import CreateFunctionModal from "../../Model/CreateFunctionModal";
import { ErrorModel } from "../../Model/ErrorModel";
import { SuccessModel } from "../../Model/SuccessModel";
import { getAssistantList } from "../../Redux/Slice/Assistant/AssistantsListSlice";
import { setUnsavedChanges } from "../../Redux/Slice/Common/SelectedTabSlice";
import { deleteFunction } from "../../Redux/Slice/Functions/DeleteFunctionSlice";
import { getFunctionDetail } from "../../Redux/Slice/Functions/GetFunctionDetailSlice";
import { getFunctionsList } from "../../Redux/Slice/Functions/GetFunctionsListSlice";
import { updateFunction } from "../../Redux/Slice/Functions/UpdateFunctionSlice";
import { FunctionListSidebar } from "./FunctionListSidebar";
import useDebounce from "../../Common/useDebounce";
import useDidMountEffect from "../../Common/useDidMountEffect";

const CreateEditFunctions = () => {
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const dispatch = useDispatch();
  const INITIAL_FORMDATA = {
    serverUrl: "",
    serverUrlToken: "",
    timeoutSeconds: "",
    headers: [],
    schema: [],
    name: "",
    description: "",
    startMessage: "",
    delayedResponseMessage: "",
    successMessage: "",
    errorMessage: "",
  };
  const [collapse, setCollapse] = useState({
    integration: true,
    details: true,
    functions: true,
    toolMessage: true,
  });
  const [formData, setFormData] = useState(INITIAL_FORMDATA);
  const [formError, setFormError] = useState({});
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasChangedModel, setHasChangedModel] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [currentSchema, setCurrentSchema] = useState(null);
  const [searchText, setSearchText] = useState("");
  const searchQuery = useDebounce(searchText, 500);
  const { functionListData, loading } = useSelector(
    (state) => state?.functionsList
  );
  const functionList =
    functionListData?.data && Array.isArray(functionListData?.data)
      ? functionListData?.data
      : [];

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message: message ?? "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const handleChangeCollapse = (name) => {
    setCollapse((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleChange = async (e) => {
    let { name, value } = e.target;
    const errorKey = `${name}Error`;
    value = value.trimStart();
    if (name === "serverUrl") {
      const isValid = validURL(value);
      if (isValid || value.trim() === "") {
        setFormError({ ...formError, [errorKey]: "" });
      } else {
        setFormError({
          ...formError,
          [errorKey]: "Please enter valid Server Url",
        });
      }
    } else if (name == "timeoutSeconds") {
      value = value.replace(/[^0-9]/g, "");
      if ((value > 1 && value < 120) || !value) {
        setFormError({ ...formError, [errorKey]: "" });
      } else if (value) {
        setFormError({
          ...formError,
          [errorKey]: "Timeout Seconds must be less than or equal to 120",
        });
      }
    } else if (name == "name") {
      const regex = /^[a-zA-Z0-9_-]{1,64}$/;
      if (regex.test(value)) {
        setFormError({ ...formError, [errorKey]: "" });
      } else {
        setFormError({
          ...formError,
          [errorKey]:
            "Name must only contain letters, numbers, underscores, or hyphens and be between 1-64 characters",
        });
      }
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const addHeaderItemData = () => {
    setFormData((prev) => ({
      ...prev,
      headers: [
        ...prev.headers,
        {
          id: uuidGenerator(),
          key: "",
          type: "",
          value: "",
        },
      ],
    }));
  };

  const handleChangeHeaderData = (e, id) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      headers: prev.headers?.map((header) => {
        if (header.id === id) {
          return { ...header, [name]: value };
        } else {
          return header;
        }
      }),
    }));
  };

  const deleteHeaderItemData = (id) => {
    setFormData((prev) => ({
      ...prev,
      headers: prev.headers?.filter((header) => header.id != id),
    }));
  };

  const addSchemaItemData = () => {
    const newUUID = uuidGenerator();
    if (formData.schema.length == 0) {
      setCurrentSchema(newUUID);
    }
    setFormData((prev) => ({
      ...prev,
      schema: [
        ...prev.schema,
        {
          id: newUUID,
          name: "",
          description: "",
          type: "",
        },
      ],
    }));
  };

  const handleChangeSchemaData = (id, itemData) => {
    setFormData((prev) => ({
      ...prev,
      schema: prev.schema?.map((schemaItem) => {
        if (schemaItem.id === id) {
          return itemData;
        } else {
          return schemaItem;
        }
      }),
    }));
    setCurrentSchema(null);
  };

  const deleteSchemaItemData = (id) => {
    setFormData((prev) => ({
      ...prev,
      schema: prev.schema?.filter((schemaItem) => schemaItem.id != id),
    }));
  };

  const updateFunctionDetails = async () => {
    let headers = {};
    let schema = {};
    let messages = [];
    formData.headers?.forEach((headerItem) => {
      headers[headerItem.key] = headerItem.value;
    });
    formData.schema?.forEach((schemaItem) => {
      schema[schemaItem.name] = {
        type: schemaItem.type,
        description: schemaItem.description,
      };
    });
    if (formData.startMessage) {
      messages.push({
        type: "request-start",
        content: formData.startMessage,
      });
    }
    if (formData.delayedResponseMessage) {
      messages.push({
        type: "request-response-delayed",
        content: formData.delayedResponseMessage,
      });
    }
    if (formData.successMessage) {
      messages.push({
        type: "request-complete",
        content: formData.successMessage,
        role: "assistant",
      });
    }
    if (formData.errorMessage) {
      messages.push({
        type: "request-failed",
        content: formData.errorMessage,
      });
    }
    let body = {
      messages: messages?.length ? messages : undefined,
      server: {
        url: formData.serverUrl,
        secret:
          formData.serverUrlToken && formData.serverUrlToken?.length
            ? formData.serverUrlToken
            : undefined,
        timeoutSeconds: parseInt(formData.timeoutSeconds) ?? 60,
        headers: formData.headers?.length ? headers : undefined,
      },
      function: {
        description:
          formData.description && formData.description?.length
            ? formData.description
            : undefined,
        async: false,
        name: formData.name,
        parameters: formData.schema?.length
          ? {
              type: "object",
              properties: schema,
            }
          : undefined,
      },
      async: false,
    };
    setIsLoading(true);
    try {
      const result = await dispatch(
        updateFunction({ id: selectedFunction?.vapiAssistanceId, body })
      );
      if (
        updateFunction.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        showSuccessMessage("Function Details Updated Successfully!", () =>
          refetchFunctionData()
        );
      } else {
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const hasFormError = useMemo(() => {
    return Object.values(formError).some((error) => error);
  }, [formError]);

  const isDisabled = useMemo(() => {
    const isHaveNotFilledHeaders = formData.headers?.length
      ? formData.headers.some(
          (header) => !header.key || !header.type || !header.value
        )
      : false;

    const isHaveNotFilledSchemas = formData?.schema?.length
      ? formData.schema.some((schema) => !schema.name || !schema.type)
      : false;

    return (
      !formData.serverUrl ||
      !formData.timeoutSeconds ||
      isHaveNotFilledHeaders ||
      isHaveNotFilledSchemas ||
      !formData.name
    );
  }, [formData]);

  const CurrentEditableSchema = ({ schemaItem }) => {
    const [currentSchemaData, setCurrentSchemaData] = useState(schemaItem);

    const handleChangeSchemaItem = (e) => {
      const { name, value } = e?.target;

      setCurrentSchemaData((prev) => ({
        ...prev,
        [name]: value,
      }));
    };

    const handleSaveSchemaItem = () => {
      handleChangeSchemaData(schemaItem?.id, currentSchemaData);
    };

    return (
      <div
        className="formCol"
        style={{
          display: "grid",
          border: "1px solid #CBCAD7",
          padding: "13px 14px",
          borderRadius: "6px",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <div className="form__Field form__Field--full">
          <input
            type="text"
            id="name"
            name="name"
            value={currentSchemaData?.name}
            onChange={handleChangeSchemaItem}
            className="form__input form__input--sm"
            placeholder="Name"
          />
        </div>

        <div className="form__Field form__Field--full">
          <input
            type="text"
            id="description"
            name="description"
            value={currentSchemaData?.description}
            onChange={handleChangeSchemaItem}
            className="form__input form__input--sm"
            placeholder="Description"
          />
        </div>

        <div
          className="formRow formRow__2"
          style={{
            borderTop: "1px solid #CBCAD7",
            alignItems: "center",
            paddingTop: 8,
          }}
        >
          <select
            className="form__select form__select--sm"
            id="type"
            name="type"
            style={{ maxWidth: "160px" }}
            value={currentSchemaData?.type}
            onChange={handleChangeSchemaItem}
          >
            <option selected="" value="" disabled>
              -Select-
            </option>
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="object">Object</option>
            <option value="array">Array</option>
          </select>
          <div className="btn__group btn__group--end">
            <button
              className="btn btn__white"
              onClick={() => deleteSchemaItemData(schemaItem?.id)}
            >
              Delete
            </button>
            <button
              className="btn btn__primary"
              disabled={!currentSchemaData?.name || !currentSchemaData?.type}
              onClick={handleSaveSchemaItem}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const refetchFunctionData = async () => {
    await dispatch(getFunctionsList());
    await getFunctionDetailById(selectedFunction?.vapiAssistanceId);
  };

  const onDelete = async () => {
    setConfirmModal(false);
    setIsLoading(true);
    const result = await dispatch(
      deleteFunction(selectedFunction?.vapiAssistanceId)
    );
    setIsLoading(false);
    if (
      deleteFunction.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      showSuccessMessage("Function Deleted Successfully!", () =>
        getFunctionsData()
      );
    } else {
      showErrorMessage(result?.payload?.error?.message);
    }
  };

  const getFunctionDetailById = async (id) => {
    setIsLoading(true);
    try {
      const result = await dispatch(getFunctionDetail(id));
      if (
        getFunctionDetail.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setHasChanged(false);
        dispatch(setUnsavedChanges(false));
        const functionDetails = result?.payload?.data;
        if (functionDetails) {
          let startMessage = INITIAL_FORMDATA.startMessage;
          let delayedResponseMessage = INITIAL_FORMDATA.delayedResponseMessage;
          let successMessage = INITIAL_FORMDATA.successMessage;
          let errorMessage = INITIAL_FORMDATA.errorMessage;
          let headers = [];
          let schemas = [];

          if (functionDetails?.messages && functionDetails?.messages?.length) {
            functionDetails?.messages?.forEach((message) => {
              if (message?.type == "request-start") {
                startMessage = message?.content;
              } else if (message?.type == "request-response-delayed") {
                delayedResponseMessage = message?.content;
              } else if (message?.type == "request-complete") {
                successMessage = message?.content;
              } else if (message?.type == "request-failed") {
                errorMessage = message?.content;
              }
            });
          }
          if (
            functionDetails?.server?.headers &&
            Object.keys(functionDetails?.server?.headers)?.length
          ) {
            Object.keys(functionDetails?.server?.headers)?.forEach((key) => {
              headers.push({
                id: uuidGenerator(),
                key: key,
                type: "string",
                value: functionDetails?.server?.headers[key],
              });
            });
          }
          if (
            functionDetails?.function?.parameters?.properties &&
            Object.keys(functionDetails?.function?.parameters?.properties)
              ?.length
          ) {
            Object.keys(
              functionDetails?.function?.parameters?.properties
            )?.forEach((key) => {
              schemas.push({
                id: uuidGenerator(),
                name: key,
                description:
                  functionDetails?.function?.parameters?.properties[key]
                    ?.description ?? "",
                type:
                  functionDetails?.function?.parameters?.properties[key]
                    ?.type ?? "",
              });
            });
          }
          setFormData({
            ...INITIAL_FORMDATA,
            serverUrl:
              functionDetails?.server?.url ?? INITIAL_FORMDATA.serverUrl,
            serverUrlToken:
              functionDetails?.server?.secret ??
              INITIAL_FORMDATA.serverUrlToken,
            timeoutSeconds:
              functionDetails?.server?.timeoutSeconds ??
              INITIAL_FORMDATA.timeoutSeconds,
            headers: headers,
            schema: schemas,
            name: functionDetails?.function?.name ?? INITIAL_FORMDATA.name,
            description:
              functionDetails?.function?.description ??
              INITIAL_FORMDATA.description,
            startMessage: startMessage,
            delayedResponseMessage: delayedResponseMessage,
            successMessage: successMessage,
            errorMessage: errorMessage,
          });
          setSelectedFunction(functionDetails);
        }
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const getFunctionsData = async () => {
    const result = await dispatch(getFunctionsList());
    if (
      getFunctionsList.fulfilled.match(result) &&
      result?.payload?.success === true
    ) {
      const functionsList =
        result?.payload?.data && Array.isArray(result?.payload?.data)
          ? result?.payload?.data
          : [];
      if (functionsList?.length) {
        getFunctionDetailById(functionsList[0]?.vapiFunctionId);
      }
    }
  };

  const changeSelection = (functionItem) => {
    getFunctionDetailById(functionItem?.vapiFunctionId);
  };

  useEffect(() => {
    getFunctionsData();
    dispatch(getAssistantList({ text: "", isDropdown: true }));
  }, []);

  useDidMountEffect(() => {
    dispatch(getFunctionsList({ keyword: searchQuery }));
  }, [searchQuery]);

  return (
    <div className="page__body">
      {(isLoading || loading) && <Loader />}
      {errorModal.visible && <ErrorModel message={errorModal.message} />}
      {successModal.visible && <SuccessModel message={successModal.message} />}
      {createModalVisible ? (
        <CreateFunctionModal
          closePopup={() => setCreateModalVisible(false)}
          onSuccess={() => {
            setCreateModalVisible(false);
            showSuccessMessage("Function added successfully!");
            getFunctionsData();
          }}
        />
      ) : null}
      {confirmModal ? (
        <ConfirmModel
          onAccept={onDelete}
          onReject={() => {
            setConfirmModal(false);
          }}
          title="Delete Function!"
          message="Are you sure you want to Delete the Function?"
        />
      ) : (
        ""
      )}
      {hasChangedModel ? (
        <ConfirmModel
          onAccept={() => {
            dispatch(setUnsavedChanges(false));
          }}
          yes={"Yes"}
          no={"No"}
          onReject={() => {
            setHasChangedModel(false);
          }}
          title="Update Changes!"
          message="Changes made will not be saved. Are you sure you want to leave the page?"
        />
      ) : null}
      {(functionList?.length || searchText || searchQuery) &&
      selectedFunction ? (
        <div className="createAssistantMain h_100">
          <FunctionListSidebar
            selectedFunction={selectedFunction}
            changeSelection={changeSelection}
            showSuccessMessage={showSuccessMessage}
            getFunctionsData={getFunctionsData}
            searchText={searchText}
            setSearchText={setSearchText}
          />
          {selectedFunction ? (
            <div className="createAssistantMain__col h_100">
              <div className="createAssistantMain__card">
                <div className="createAssistantHeader createAssistantHeader--flexStart">
                  <div
                    className="createAssistantHeader__info"
                    style={{ width: "80%" }}
                  >
                    <div className="title">
                      <div className="createAssistantHeader__info__content">
                        <h1 className="title title--h3">
                          {selectedFunction?.function?.name}
                        </h1>
                      </div>
                    </div>
                    <p className="para" style={{ fontWeight: "600" }}>
                      {selectedFunction?.function?.description}
                    </p>
                  </div>
                  <div className="btn__group">
                    <button
                      className="btn btn__primary"
                      disabled={isDisabled || hasFormError}
                      onClick={updateFunctionDetails}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn__danger btn__outlined"
                      onClick={() => setConfirmModal(true)}
                    >
                      <SolidTrashIcon />
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              <div className="createAssistantMain__card createAssistantMain__card--second">
                <div className="createAssistantBody">
                  <div className="accordion">
                    <div
                      class={`accordion__card ${
                        collapse.integration ? "accordion__card--active" : ""
                      }`}
                    >
                      <div
                        className="accordion__head"
                        onClick={() => handleChangeCollapse("integration")}
                      >
                        <h1 className="title title--h4">Integration</h1>
                        <button className="btnArrow">
                          <UpArrowIcon />
                        </button>
                      </div>
                      <div className="accordion__body">
                        <div
                          className="createFormGrid"
                          style={{ maxHeight: "none" }}
                        >
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="serverUrl"
                                  className="form__label form__label--flex"
                                >
                                  <span>
                                    Server url
                                    <sup className="form__label__asterisk">
                                      *
                                    </sup>
                                  </span>
                                </label>
                                <input
                                  type="text"
                                  id="serverUrl"
                                  name="serverUrl"
                                  value={formData.serverUrl}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder="Server url"
                                />
                                {formError?.serverUrlError ? (
                                  <div className="form__error">
                                    {formError?.serverUrlError}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="serverUrlToken"
                                  className="form__label form__label--flex"
                                >
                                  Server url secret token
                                </label>
                                <input
                                  type="password"
                                  id="serverUrlToken"
                                  name="serverUrlToken"
                                  value={formData.serverUrlToken}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder="Server url secret token"
                                />
                                {formError?.serverUrlTokenError ? (
                                  <div className="form__error">
                                    {formError?.serverUrlTokenError}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="timeoutSeconds"
                                  className="form__label form__label--flex"
                                >
                                  <span>
                                    Timeout seconds
                                    <sup className="form__label__asterisk">
                                      *
                                    </sup>
                                  </span>
                                </label>
                                <input
                                  type="text"
                                  id="timeoutSeconds"
                                  name="timeoutSeconds"
                                  value={formData.timeoutSeconds}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder="Timeout seconds"
                                  maxLength={3}
                                />
                                {formError?.timeoutSecondsError ? (
                                  <div className="form__error">
                                    {formError?.timeoutSecondsError}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <p
                                  for="timeoutSeconds"
                                  className="para"
                                  style={{ marginBottom: "8px" }}
                                >
                                  Headers
                                </p>
                                {formData.headers.map(
                                  (headerItemData, index) => (
                                    <div className="formRow formRow__3">
                                      <div className="formCol">
                                        <div className="form__Field form__Field--full">
                                          <label
                                            for="key"
                                            className="form__label"
                                          >
                                            Key
                                          </label>
                                          <input
                                            type="text"
                                            id="key"
                                            name="key"
                                            value={headerItemData.key}
                                            onChange={(e) =>
                                              handleChangeHeaderData(
                                                e,
                                                headerItemData.id
                                              )
                                            }
                                            className="form__input form__input--sm"
                                            placeholder="Key"
                                          />
                                        </div>
                                      </div>
                                      <div className="formCol">
                                        <div className="form__Field form__Field--full">
                                          <label
                                            for="type"
                                            className="form__label"
                                          >
                                            Type
                                          </label>
                                          <select
                                            className="form__select form__select--sm"
                                            id="type"
                                            name="type"
                                            value={headerItemData.type}
                                            onChange={(e) =>
                                              handleChangeHeaderData(
                                                e,
                                                headerItemData.id
                                              )
                                            }
                                          >
                                            <option
                                              selected=""
                                              value=""
                                              disabled
                                            >
                                              -Select-
                                            </option>
                                            <option value="string">
                                              String
                                            </option>
                                            <option value="number">
                                              Number
                                            </option>
                                            <option value="boolean">
                                              Boolean
                                            </option>
                                            <option value="object">
                                              Object
                                            </option>
                                          </select>
                                        </div>
                                      </div>
                                      <div className="formCol">
                                        <div className="form__Field form__Field--full">
                                          <label
                                            for="value"
                                            className="form__label"
                                          >
                                            Value
                                          </label>
                                          <div className="form__group__flex">
                                            <input
                                              type="text"
                                              id="value"
                                              name="value"
                                              value={headerItemData.value}
                                              onChange={(e) =>
                                                handleChangeHeaderData(
                                                  e,
                                                  headerItemData.id
                                                )
                                              }
                                              className="form__input form__input--sm"
                                              placeholder="Value"
                                            />
                                            <button
                                              type="button"
                                              className="btn btn__danger btn__outlined"
                                              style={{ height: "44px" }}
                                              onClick={() =>
                                                deleteHeaderItemData(
                                                  headerItemData.id
                                                )
                                              }
                                            >
                                              <DeleteIcon />
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <button
                              type="button"
                              className="btn btn__outlined"
                              onClick={addHeaderItemData}
                            >
                              Add Row
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      class={`accordion__card ${
                        collapse.details ? "accordion__card--active" : ""
                      }`}
                    >
                      <div
                        className="accordion__head"
                        onClick={() => handleChangeCollapse("details")}
                      >
                        <h1 className="title title--h4">Details</h1>
                        <button className="btnArrow">
                          <UpArrowIcon />
                        </button>
                      </div>
                      <div className="accordion__body">
                        <div
                          className="createFormGrid"
                          style={{ maxHeight: "none" }}
                        >
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="name"
                                  className="form__label form__label--flex"
                                >
                                  <span>
                                    Name
                                    <sup className="form__label__asterisk">
                                      *
                                    </sup>
                                  </span>
                                </label>
                                <input
                                  type="text"
                                  id="name"
                                  name="name"
                                  value={formData.name}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder="What is your function name?"
                                />
                                {formError?.nameError ? (
                                  <div className="form__error">
                                    {formError?.nameError}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="description"
                                  className="form__label form__label--flex"
                                >
                                  Description
                                </label>
                                <textarea
                                  id="description"
                                  className="form__textarea"
                                  name="description"
                                  maxLength={500}
                                  value={formData.description}
                                  onChange={handleChange}
                                  placeholder="Describe the function, its purpose, its uses etc."
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      class={`accordion__card ${
                        collapse.functions ? "accordion__card--active" : ""
                      }`}
                    >
                      <div
                        className="accordion__head"
                        onClick={() => handleChangeCollapse("functions")}
                      >
                        <h1 className="title title--h4">Function</h1>
                        <button className="btnArrow">
                          <UpArrowIcon />
                        </button>
                      </div>
                      <div className="accordion__body">
                        <div
                          className="createFormGrid"
                          style={{ maxHeight: "none" }}
                        >
                          <div className="createFormGrid__row">
                            <button
                              type="button"
                              className="btn btn__outlined"
                              onClick={addSchemaItemData}
                            >
                              <AddIcon color="#111" />
                              Properties
                            </button>
                          </div>
                          {formData.schema?.map((schemaItem) => (
                            <div className="createFormGrid__row">
                              {currentSchema == schemaItem.id ? (
                                <CurrentEditableSchema
                                  schemaItem={schemaItem}
                                />
                              ) : (
                                <div className="formCol">
                                  <div
                                    className="formRow formRow__2"
                                    style={{
                                      border: "1px solid #CBCAD7",
                                      padding: "13px 14px",
                                      borderRadius: "6px",
                                      alignItems: "center",
                                    }}
                                    onClick={() =>
                                      setCurrentSchema(schemaItem.id)
                                    }
                                  >
                                    <h5 className="title title--h6">
                                      {schemaItem?.name}
                                    </h5>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      <CollapseIcon />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div
                      class={`accordion__card ${
                        collapse.toolMessage ? "accordion__card--active" : ""
                      }`}
                    >
                      <div
                        className="accordion__head"
                        onClick={() => handleChangeCollapse("toolMessage")}
                      >
                        <h1 className="title title--h4">Tool messages</h1>
                        <button className="btnArrow">
                          <UpArrowIcon />
                        </button>
                      </div>
                      <div className="accordion__body">
                        <div
                          className="createFormGrid"
                          style={{ maxHeight: "none" }}
                        >
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="startMessage"
                                  className="form__label form__label--flex"
                                >
                                  Start message
                                </label>
                                <input
                                  type="text"
                                  id="startMessage"
                                  name="startMessage"
                                  value={formData.startMessage}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder='Message to show when the tool starts running (e.g. "Fetching user information...")'
                                />
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="delayedResponseMessage"
                                  className="form__label form__label--flex"
                                >
                                  Delayed response message
                                </label>
                                <input
                                  type="text"
                                  id="delayedResponseMessage"
                                  name="delayedResponseMessage"
                                  value={formData.delayedResponseMessage}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder="Message to show when the response is taking longer than expected"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="successMessage"
                                  className="form__label form__label--flex"
                                >
                                  Success message
                                </label>
                                <input
                                  type="text"
                                  id="successMessage"
                                  name="successMessage"
                                  value={formData.successMessage}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder="Message to show when the tool completes successfully"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="createFormGrid__row">
                            <div className="formCol">
                              <div className="form__Field form__Field--full">
                                <label
                                  for="errorMessage"
                                  className="form__label form__label--flex"
                                >
                                  Error message
                                </label>
                                <input
                                  type="text"
                                  id="errorMessage"
                                  name="errorMessage"
                                  value={formData.errorMessage}
                                  onChange={handleChange}
                                  className="form__input form__input--sm"
                                  placeholder="Message to show if the tool execution fails"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="createAssistant">
          <div className="createAssistant__card">
            <div className="createAssistant__details">
              <h2 className="title">Create Function</h2>
              <div className="icon">
                <FunctionIcon />
              </div>
              <p className="para">
                Assistants are voice AI chat bots used for integrations into
                your applications.You can fully configure them to your
                businesses needs, and we support all major models and providers.
              </p>
              <div className="btn__group">
                <button
                  className="btn btn__primary"
                  onClick={() => setCreateModalVisible(true)}
                >
                  <AddIcon />
                  Create Function
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateEditFunctions;
